import React, { useContext } from "react";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import { CrmEntity, SalesActivityTypeCount } from "./activitiesReportService";
import { AuthContext } from "../../../../context/authcontext";
import { getLifecycleEntityLabel } from "../../../../utils/lifecycleUtils";

interface ActivitiesReportFiltersProps {
  loading: boolean;
  dateRange: [Date | null, Date | null];
  onDateRangeChange: (start: Date | null, end: Date | null) => void;
  entityOptions: CrmEntity[];
  crmEntityId: number | null;
  onEntityChange: (entityId: number | null) => void;
  typeOptions: SalesActivityTypeCount[];
  crmSalesActivityTypeId: number | null;
  onTypeChange: (typeId: number | null) => void;
  crmSalesActivityTypeStatusId: number | null;
  onStatusChange: (statusId: number | null) => void;
  onClear?: () => void;
}

const dateRangeSettings = {
  endDate: new Date(),
  ranges: {
    "Last 30 Days": [
      new Date(new Date().setDate(new Date().getDate() - 30)),
      new Date(),
    ],
    "Last 7 Days": [
      new Date(new Date().setDate(new Date().getDate() - 7)),
      new Date(),
    ],
    "This Month": [
      new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      new Date(),
    ],
    "Last Month": [
      new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1),
      new Date(new Date().getFullYear(), new Date().getMonth(), 0),
    ],
    Today: [new Date(), new Date()],
  },
  startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
  timePicker: false,
};

const ActivitiesReportFilters: React.FC<ActivitiesReportFiltersProps> = ({
  loading,
  dateRange,
  onDateRangeChange,
  entityOptions,
  crmEntityId,
  onEntityChange,
  typeOptions,
  crmSalesActivityTypeId,
  onTypeChange,
  crmSalesActivityTypeStatusId,
  onStatusChange,
}) => {
  const { lifecycleTypeNames } = useContext(AuthContext);
  const selectedType = typeOptions.find(
    (t) => t.crm_sales_activity_type_id === crmSalesActivityTypeId
  );
  const statusOptions = selectedType?.statuses || [];
  const hasActiveFilters =
    !!dateRange[0] ||
    !!dateRange[1] ||
    crmEntityId !== null ||
    crmSalesActivityTypeId !== null ||
    crmSalesActivityTypeStatusId !== null;

  return (
    <div className="card mb-4">
      <div className="card-header">
        <div className="d-flex align-items-center justify-content-between">
          <h6 className="mb-0">
            <i className="ti ti-filter me-2" />
            Activity Filters
          </h6>
        </div>
      </div>
      <div className="card-body">
        <div className="row g-3">
          <div className="col-md-6 col-lg-3">
            <label className="form-label">Date Range</label>
            <div className={`icon-form ${dateRange[0] ? "filter-active" : ""}`}>
              <span className="form-icon">
                <i className="ti ti-calendar" />
              </span>
              <DateRangePicker
                initialSettings={{ ...dateRangeSettings, autoUpdateInput: false }}
                onApply={(_event, picker) => {
                  onDateRangeChange(picker.startDate.toDate(), picker.endDate.toDate());
                }}
                onCancel={() => onDateRangeChange(null, null)}
              >
                <input
                  className={`form-control bookingrange table-filters-daterange ${
                    dateRange[0] ? "filter-active-input" : ""
                  }`}
                  type="text"
                  placeholder="Last 30 Days"
                  readOnly
                  value={
                    dateRange[0] && dateRange[1]
                      ? `${dateRange[0].toLocaleDateString()} - ${dateRange[1].toLocaleDateString()}`
                      : ""
                  }
                  onChange={() => {}}
                  style={{ paddingLeft: "34px", cursor: "pointer" }}
                  disabled={loading}
                />
              </DateRangePicker>
            </div>
          </div>

          <div className="col-md-6 col-lg-3">
            <label className="form-label">CRM Entity</label>
            <select
              className="form-select"
              value={crmEntityId ?? ""}
              onChange={(e) =>
                onEntityChange(e.target.value ? Number(e.target.value) : null)
              }
              disabled={loading}
            >
              <option value="">All Entities</option>
              {entityOptions.map((entity) => (
                <option key={entity.crm_entity_id} value={entity.crm_entity_id}>
                  {getLifecycleEntityLabel(entity.crm_entity_display_name, lifecycleTypeNames)}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-6 col-lg-3">
            <label className="form-label">Activity Type</label>
            <select
              className="form-select"
              value={crmSalesActivityTypeId ?? ""}
              onChange={(e) =>
                onTypeChange(e.target.value ? Number(e.target.value) : null)
              }
              disabled={loading}
            >
              <option value="">All Activity Types</option>
              {typeOptions.map((type) => (
                <option
                  key={type.crm_sales_activity_type_id}
                  value={type.crm_sales_activity_type_id}
                >
                  {type.crm_sales_activity_type_display_name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-6 col-lg-3">
            <label className="form-label">Status</label>
            <select
              className="form-select"
              value={crmSalesActivityTypeStatusId ?? ""}
              onChange={(e) => {
                const val = e.target.value ? Number(e.target.value) : null;
                onStatusChange(val);
                if (val && !selectedType) {
                  const parentType = typeOptions.find((t) =>
                    t.statuses?.some((s) => s.crm_sales_activity_type_status_id === val)
                  );
                  if (parentType) {
                    onTypeChange(parentType.crm_sales_activity_type_id);
                  }
                }
              }}
              disabled={loading}
            >
              <option value="">All Statuses</option>
              {selectedType ? (
                statusOptions.map((status) => (
                  <option
                    key={status.crm_sales_activity_type_status_id}
                    value={status.crm_sales_activity_type_status_id}
                  >
                    {status.crm_sales_activity_type_status_display_name}
                  </option>
                ))
              ) : (
                typeOptions.map((type) => (
                  <optgroup
                    key={type.crm_sales_activity_type_id}
                    label={type.crm_sales_activity_type_display_name}
                  >
                    {type.statuses?.map((status) => (
                      <option
                        key={status.crm_sales_activity_type_status_id}
                        value={status.crm_sales_activity_type_status_id}
                      >
                        {status.crm_sales_activity_type_status_display_name}
                      </option>
                    ))}
                  </optgroup>
                ))
              )}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivitiesReportFilters;
